import React from "react";

interface BienestarScoreDisplayProps {
  scores: Record<string, any>;
  interpretations: Record<string, any>;
}

const BienestarScoreDisplay: React.FC<BienestarScoreDisplayProps> = ({ 
  scores, 
  interpretations 
}) => {
  // Calculate total average score across all domains
  const calculateTotalScore = (): { average: number; percentage: number } => {
    const scoreEntries = Object.values(scores);
    if (scoreEntries.length === 0) return { average: 0, percentage: 0 };
    
    let totalAverage = 0;
    let totalPercentage = 0;
    
    scoreEntries.forEach((score: any) => {
      totalAverage += score.average || 0;
      totalPercentage += score.percentage || 0;
    });
    
    return {
      average: parseFloat((totalAverage / scoreEntries.length).toFixed(1)),
      percentage: parseFloat((totalPercentage / scoreEntries.length).toFixed(1))
    };
  };

  // Helper function to get color based on percentage score
  const getScoreColor = (percentage: number): string => {
    if (percentage < 50) return "text-red-500";
    if (percentage < 70) return "text-yellow-500";
    return "text-green-500";
  };

  // Helper function to get background color based on percentage score
  const getBarColor = (percentage: number): string => {
    if (percentage < 50) return "bg-red-500";
    if (percentage < 70) return "bg-yellow-500";
    return "bg-green-500";
  };

  // Helper function to get interpretation text
  const getInterpretationText = (key: string, percentage: number): string => {
    // If we have an interpretation from the API, use that
    if (interpretations && interpretations[key]) {
      return interpretations[key];
    }
    
    // Otherwise generate a default interpretation based on score
    if (percentage < 50) {
      return "Área de mejora prioritaria";
    } else if (percentage < 70) {
      return "Área con potencial de mejora";
    } else {
      return "Área fortalecida";
    }
  };
  
  // Get total score
  const totalScore = calculateTotalScore();
  const totalScoreColor = getScoreColor(totalScore.percentage);
  
  // Debug log
  console.log("BIENESTAR scores:", scores);
  console.log("BIENESTAR scores type:", typeof scores);
  console.log("BIENESTAR scores is array:", Array.isArray(scores));
  console.log("BIENESTAR scores keys:", Object.keys(scores));
  console.log("BIENESTAR interpretations:", interpretations);
  console.log("BIENESTAR total score:", totalScore);

  // If scores is empty or not an object, display a message
  if (!scores || typeof scores !== 'object' || Object.keys(scores).length === 0) {
    return (
      <div className="text-center py-4">
        <p className="text-gray-600 dark:text-gray-400">
          No hay puntuaciones disponibles para el cuestionario de bienestar
        </p>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
          Datos recibidos: {JSON.stringify(scores)}
        </p>
      </div>
    );
  }

  // Sort domains by score (ascending) to identify areas of improvement
  const sortedScores = Object.entries(scores)
    .map(([key, value]) => ({ key, ...value as any }))
    .sort((a, b) => (a.percentage || 0) - (b.percentage || 0));

  // Categorize domains by level (areas for improvement, moderate, and strong areas)
  const improvementAreas = sortedScores.filter(score => (score.percentage || 0) < 70);
  const strongAreas = sortedScores.filter(score => (score.percentage || 0) >= 70);

  return (
    <div className="space-y-8">
      {/* Overall wellbeing score section */}
      <div className="bg-white dark:bg-navy-800 rounded-xl p-5 shadow-md mb-6">
        <h5 className="text-lg font-bold text-navy-700 dark:text-white mb-2">
          Índice de Bienestar General
        </h5>
        
        <div className="flex flex-wrap justify-between items-center mb-3">
          <div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Promedio de todas las áreas evaluadas
            </p>
          </div>
          <div className="flex flex-col items-end">
            <span className={`text-3xl font-bold ${totalScoreColor}`}>
              {totalScore.percentage}%
            </span>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              Puntuación media: {totalScore.average}/10
            </span>
          </div>
        </div>
        
        <div className="w-full bg-gray-200 dark:bg-navy-700 rounded-full h-4 mb-4">
          <div 
            className={`h-4 rounded-full ${getBarColor(totalScore.percentage)}`} 
            style={{ width: `${totalScore.percentage}%` }}
          ></div>
        </div>
        
        <div className="flex justify-between text-xs text-gray-500 px-1">
          <span>0%</span>
          <span>50%</span>
          <span>100%</span>
        </div>
      </div>

      {/* Areas for improvement */}
      {improvementAreas.length > 0 && (
        <div className="mb-8">
          <h5 className="text-lg font-bold text-navy-700 dark:text-white mb-3">
            Áreas de Oportunidad
          </h5>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Aspectos del bienestar que pueden beneficiarse de mayor atención
          </p>

          <div className="space-y-4">
            {improvementAreas.map((score) => {
              const percentage = score.percentage || 0;
              const textColor = getScoreColor(percentage);
              const barColor = getBarColor(percentage);
              const interpretationText = getInterpretationText(score.key, percentage);
              
              return (
                <div key={score.key} className="bg-white dark:bg-navy-800 rounded-lg p-4 shadow-sm">
                  <div className="flex flex-wrap justify-between items-center mb-1">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {score.name}
                    </span>
                    <div className="flex items-center">
                      <span className={`text-sm font-bold ${textColor}`}>
                        {percentage.toFixed(1)}%
                      </span>
                    </div>
                  </div>
                  
                  <div className="w-full bg-gray-200 dark:bg-navy-700 rounded-full h-2.5 mb-2">
                    <div 
                      className={`h-2.5 rounded-full ${barColor}`} 
                      style={{ width: `${percentage}%` }}
                    ></div>
                  </div>
                  
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {score.description}
                  </p>
                  
                  <p className={`text-xs font-medium mt-2 ${textColor}`}>
                    {interpretationText}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Strong areas */}
      {strongAreas.length > 0 && (
        <div className="mb-8">
          <h5 className="text-lg font-bold text-navy-700 dark:text-white mb-3">
            Áreas de Fortaleza
          </h5>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Aspectos del bienestar que muestran buenos resultados
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {strongAreas.map((score) => {
              const percentage = score.percentage || 0;
              const textColor = getScoreColor(percentage);
              const barColor = getBarColor(percentage);
              
              return (
                <div key={score.key} className="bg-white dark:bg-navy-800 rounded-lg p-4 shadow-sm">
                  <div className="flex flex-wrap justify-between items-center mb-1">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {score.name}
                    </span>
                    <span className={`text-sm font-bold ${textColor}`}>
                      {percentage.toFixed(1)}%
                    </span>
                  </div>
                  
                  <div className="w-full bg-gray-200 dark:bg-navy-700 rounded-full h-2.5 mb-2">
                    <div 
                      className={`h-2.5 rounded-full ${barColor}`} 
                      style={{ width: `${percentage}%` }}
                    ></div>
                  </div>
                  
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {score.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="bg-white dark:bg-navy-800 rounded-xl p-5 shadow-md">
        <h5 className="text-md font-bold text-navy-700 dark:text-white mb-3">
          Interpretación de Resultados
        </h5>
        <div className="space-y-3">
          <div className="flex items-center">
            <div className="w-3 h-3 rounded-full bg-green-500 mr-2"></div>
            <span className="text-sm">
              <span className="font-medium">70-100%:</span> Áreas fortalecidas que contribuyen positivamente al bienestar
            </span>
          </div>
          <div className="flex items-center">
            <div className="w-3 h-3 rounded-full bg-yellow-500 mr-2"></div>
            <span className="text-sm">
              <span className="font-medium">50-69%:</span> Áreas con potencial de mejora que requieren atención moderada
            </span>
          </div>
          <div className="flex items-center">
            <div className="w-3 h-3 rounded-full bg-red-500 mr-2"></div>
            <span className="text-sm">
              <span className="font-medium">&lt;50%:</span> Áreas de mejora prioritaria que requieren atención inmediata
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BienestarScoreDisplay; 